/**
 * Copyright (c) 2026, CheckPlus Limited
 */

/**
 * Visual constants for the Plug marketing site.
 *
 * Everything here is language-independent — colours, tints, shares, unit costs
 * and the icon each card carries. All wording lives in copyEn / copyZh / copyCn
 * and is matched to these rows by `id`, so a translation never has to restate a
 * hex code and the three languages can never drift apart visually.
 */

/* Cause tiles on the donor page. `count` is the nonprofit tally shown on the
   tile; see the placeholder note in copyEn.js. */
export const CAUSES = [
  { id: "homeless", color: "#2b91d6", tint: "#e6f5ff", count: 0 },
  { id: "poverty", color: "#45b58a", tint: "#e8f7f1", count: 0 },
  { id: "disability", color: "#175b89", tint: "#e7eef3", count: 0 },
  { id: "youth", color: "#007ec1", tint: "#e4f2fa", count: 0 },
  { id: "mental", color: "#41b2fd", tint: "#e9f6ff", count: 0 },
  { id: "elderly", color: "#2482c4", tint: "#e6f1f9", count: 0 },
  { id: "environment", color: "#3aa47f", tint: "#e8f5f0", count: 0 },
  { id: "animals", color: "#6fb2dc", tint: "#eaf4fb", count: 0 },
];

/* The three nonprofits the giving calculator divides across. `share` is the
   slice of the monthly total, `unit` the published cost of one outcome. */
export const OUTCOMES = [
  { id: "shelter", mono: "HS", color: "#2b91d6", tint: "#e6f5ff", share: 0.4, unit: 120 },
  { id: "food", mono: "KF", color: "#45b58a", tint: "#e8f7f1", share: 0.35, unit: 25 },
  { id: "counselling", mono: "QH", color: "#41b2fd", tint: "#e9f6ff", share: 0.25, unit: 700 },
];

export const CALC_PRESETS = [100, 200, 500, 1000];

export const CALC_DEFAULT = 200;

/* The illustrative "one payment, four nonprofits" panel. */
export const SPLIT_ROWS = [
  { id: "shelter", color: "#2b91d6", pct: 35, amount: 140 },
  { id: "food", color: "#45b58a", pct: 30, amount: 120 },
  { id: "counselling", color: "#41b2fd", pct: 20, amount: 80 },
  { id: "brightSteps", color: "#175b89", pct: 15, amount: 60 },
];

export const IMPACT_COLORS = ["#2b91d6", "#45b58a", "#41b2fd", "#175b89"];

/* CheckPlus integration cards alternate a round and a squared dot. */
export const CP_FEATURE_DOTS = ["50%", "3px", "50%", "3px", "50%", "3px", "50%"];

export const PAGES = { ABOUT: "about", DONORS: "donors", BUSINESS: "business", NONPROFIT: "nonprofit" };


/**
 * The sections each page can be jumped to, in the order they appear. Used for
 * the footer's on-this-page links; labels live in copy under `sectionLabels`,
 * keyed by these same ids.
 *
 * "calculator" and "faq" are only present when their section is switched on,
 * so the footer filters those two against the props.
 */
export const PAGE_SECTIONS = {
  [PAGES.ABOUT]: ["flow", "sides", "values", "faq"],
  [PAGES.DONORS]: ["how", "calculator", "causes", "impact", "faq"],
  [PAGES.BUSINESS]: ["rules", "customers", "directory", "faq"],
  [PAGES.NONPROFIT]: ["why", "dashboard", "checkplus", "join", "faq"],
};

/* DOM id for a section, so the markup and the footer links cannot disagree. */
export const sectionId = (key) => "plug-" + key;

/* Each language named in itself, so the list reads the same whichever page you
   are on and needs no translation. */
export const LANGUAGE_LABELS = { zh: "繁體中文", cn: "简体中文", en: "English" };

export const fmt = (n) => "HK$" + Math.round(n).toLocaleString("en-US");

/* Initials for a business row. Words of two letters or fewer are skipped so
   "Kwun Tong Print Co." reads KT rather than KC. */
export const mono = (name) =>
  name
    .split(" ")
    .filter((w) => w.length > 2)
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
